import type { components } from '@awj/api-client';

type Detail = components['schemas']['AdminProductResponse'];
type SaveProduct = components['schemas']['SaveProductRequest'];

/**
 * The product editor's own shape.
 *
 * Not the API's request type. Two things differ, and both matter:
 *
 * Numbers are held as *strings*, because a number input mid-edit is legitimately empty or "1."
 * and coercing on every keystroke fights the person typing — the field empties itself, or jumps
 * to 0, the moment they clear it to type something else.
 *
 * Options and variants are held with stable local ids. The API identifies a variant's selections
 * by *position* in the options array (`optionIndex`/`valueIndex`), which is fine to send and
 * impossible to edit against: deleting the first option would silently repoint every variant.
 * Ids here, positions computed once at the end.
 */

export interface OptionValueDraft {
  key: string;
  valueAr: string;
  valueEn: string;
}

export interface OptionDraft {
  key: string;
  nameAr: string;
  nameEn: string;
  values: OptionValueDraft[];
}

export interface VariantDraft {
  key: string;
  sku: string;
  priceDelta: string;
  stockQuantity: string;
  isActive: boolean;
  /** One chosen value key per option key. */
  selections: Record<string, string>;
}

export interface ProductForm {
  nameAr: string;
  nameEn: string;
  shortDescriptionAr: string;
  shortDescriptionEn: string;
  descriptionAr: string;
  descriptionEn: string;
  slugAr: string;
  slugEn: string;
  sku: string;
  price: string;
  compareAtPrice: string;
  stockQuantity: string;
  lowStockThreshold: string;
  isFeatured: boolean;
  status: string;
  categoryId: string;
  metaTitleAr: string;
  metaTitleEn: string;
  metaDescriptionAr: string;
  metaDescriptionEn: string;
  options: OptionDraft[];
  variants: VariantDraft[];
}

let counter = 0;

/** A key that is unique within this editing session. Never sent. */
export function newKey(prefix: string): string {
  counter += 1;

  return `${prefix}-${counter}`;
}

export const EMPTY_PRODUCT: ProductForm = {
  nameAr: '',
  nameEn: '',
  shortDescriptionAr: '',
  shortDescriptionEn: '',
  descriptionAr: '',
  descriptionEn: '',
  slugAr: '',
  slugEn: '',
  sku: '',
  price: '',
  compareAtPrice: '',
  stockQuantity: '0',
  lowStockThreshold: '5',
  isFeatured: false,
  status: 'Draft',
  categoryId: '',
  metaTitleAr: '',
  metaTitleEn: '',
  metaDescriptionAr: '',
  metaDescriptionEn: '',
  options: [],
  variants: [],
};

export function toForm(product: Detail): ProductForm {
  const options: OptionDraft[] = product.options.map((option) => ({
    key: newKey('opt'),
    nameAr: option.nameAr,
    nameEn: option.nameEn,
    values: option.values.map((value) => ({
      key: newKey('val'),
      valueAr: value.valueAr,
      valueEn: value.valueEn,
    })),
  }));

  /*
   * Variants arrive referencing option values by database id; the editor works in local keys.
   * Mapping through position is safe here because the response's options and values are in the
   * same order they were saved in.
   */
  const valueKeyById = new Map<number, { optionKey: string; valueKey: string }>();

  product.options.forEach((option, optionIndex) => {
    option.values.forEach((value, valueIndex) => {
      valueKeyById.set(value.id, {
        optionKey: options[optionIndex]!.key,
        valueKey: options[optionIndex]!.values[valueIndex]!.key,
      });
    });
  });

  return {
    nameAr: product.nameAr,
    nameEn: product.nameEn,
    shortDescriptionAr: product.shortDescriptionAr ?? '',
    shortDescriptionEn: product.shortDescriptionEn ?? '',
    descriptionAr: product.descriptionAr ?? '',
    descriptionEn: product.descriptionEn ?? '',
    slugAr: product.slugAr,
    slugEn: product.slugEn,
    sku: product.sku,
    price: String(product.price),
    compareAtPrice: product.compareAtPrice == null ? '' : String(product.compareAtPrice),
    stockQuantity: String(product.stockQuantity),
    lowStockThreshold: String(product.lowStockThreshold),
    isFeatured: product.isFeatured,
    status: product.status,
    categoryId: String(product.categoryId),
    metaTitleAr: product.metaTitleAr ?? '',
    metaTitleEn: product.metaTitleEn ?? '',
    metaDescriptionAr: product.metaDescriptionAr ?? '',
    metaDescriptionEn: product.metaDescriptionEn ?? '',
    options,
    variants: product.variants.map((variant) => ({
      key: newKey('var'),
      sku: variant.sku,
      priceDelta: String(variant.priceDelta),
      stockQuantity: String(variant.stockQuantity),
      isActive: variant.isActive,
      selections: Object.fromEntries(
        variant.optionValueIds.flatMap((id) => {
          const found = valueKeyById.get(id);

          return found ? [[found.optionKey, found.valueKey]] : [];
        }),
      ),
    })),
  };
}

function decimal(value: string): number {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
}

export function toRequest(form: ProductForm): SaveProduct {
  return {
    nameAr: form.nameAr.trim(),
    nameEn: form.nameEn.trim(),
    shortDescriptionAr: form.shortDescriptionAr.trim() || null,
    shortDescriptionEn: form.shortDescriptionEn.trim() || null,
    descriptionAr: form.descriptionAr.trim() || null,
    descriptionEn: form.descriptionEn.trim() || null,
    slugAr: form.slugAr.trim(),
    slugEn: form.slugEn.trim(),
    sku: form.sku.trim(),
    price: decimal(form.price),
    compareAtPrice: form.compareAtPrice.trim() === '' ? null : decimal(form.compareAtPrice),
    stockQuantity: Math.trunc(decimal(form.stockQuantity)),
    lowStockThreshold: Math.trunc(decimal(form.lowStockThreshold)),
    isFeatured: form.isFeatured,
    status: form.status,
    categoryId: Number(form.categoryId),
    metaTitleAr: form.metaTitleAr.trim() || null,
    metaTitleEn: form.metaTitleEn.trim() || null,
    metaDescriptionAr: form.metaDescriptionAr.trim() || null,
    metaDescriptionEn: form.metaDescriptionEn.trim() || null,

    options: form.options.map((option) => ({
      nameAr: option.nameAr.trim(),
      nameEn: option.nameEn.trim(),
      values: option.values.map((value) => ({
        valueAr: value.valueAr.trim(),
        valueEn: value.valueEn.trim(),
      })),
    })),

    /*
     * Positions, computed here and nowhere else.
     *
     * A variant whose selection refers to an option or value that has since been deleted is
     * dropped rather than sent with a position that means something else — the alternative is a
     * request the API accepts and a product that quietly sells the wrong thing.
     */
    variants: form.variants.flatMap((variant) => {
      const selections = form.options.flatMap((option, optionIndex) => {
        const chosen = variant.selections[option.key];
        const valueIndex = option.values.findIndex((value) => value.key === chosen);

        return valueIndex === -1 ? [] : [{ optionIndex, valueIndex }];
      });

      if (selections.length !== form.options.length) {
        return [];
      }

      return [{
        sku: variant.sku.trim(),
        priceDelta: decimal(variant.priceDelta),
        stockQuantity: Math.trunc(decimal(variant.stockQuantity)),
        isActive: variant.isActive,
        selections,
      }];
    }),
  };
}

/**
 * A URL-safe slug from a name.
 *
 * Arabic letters are kept as they are — the shop's Arabic URLs are Arabic, and transliterating
 * them would produce something nobody recognises. Only whitespace and punctuation go.
 */
export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/['"’”]/g, '')
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Every combination of the current options, as variant drafts.
 *
 * Existing rows are kept — matched by their selections — so regenerating after adding a grind
 * does not wipe the stock counts and SKUs already entered against the sizes.
 */
export function buildMatrix(form: ProductForm): VariantDraft[] {
  if (form.options.length === 0 || form.options.some((option) => option.values.length === 0)) {
    return [];
  }

  const combinations = form.options.reduce<Record<string, string>[]>(
    (rows, option) =>
      rows.flatMap((row) => option.values.map((value) => ({ ...row, [option.key]: value.key }))),
    [{}],
  );

  /*
   * Rows are matched on the options they have in common, not on the full set.
   *
   * Matching on the full set means an existing row — which knows nothing about an option added a
   * moment ago — matches no new combination at all, and every SKU and stock count already typed
   * is silently replaced with a blank row. Adding a second option to a product mid-setup is
   * exactly when that hurts most.
   *
   * Each surviving row is reused once, for the first combination it agrees with. The rest of the
   * expansion starts empty, because a SKU must be unique and a stock count copied across four
   * rows would quadruple the shop's stock on paper.
   */
  const shared = form.options.filter((option) =>
    form.variants.some((variant) => option.key in variant.selections),
  );

  const signature = (selections: Record<string, string>) =>
    shared.map((option) => selections[option.key] ?? '').join('|');

  const unclaimed = new Map<string, VariantDraft[]>();

  for (const variant of form.variants) {
    const key = signature(variant.selections);

    unclaimed.set(key, [...(unclaimed.get(key) ?? []), variant]);
  }

  return combinations.map((selections) => {
    const queue = unclaimed.get(signature(selections));
    const found = queue?.shift();

    return found
      ? { ...found, selections }
      : {
          key: newKey('var'),
          sku: '',
          priceDelta: '0',
          stockQuantity: '0',
          isActive: true,
          selections,
        };
  });
}

export function variantLabel(form: ProductForm, variant: VariantDraft, arabic: boolean): string {
  return form.options
    .map((option) => {
      const value = option.values.find((candidate) => candidate.key === variant.selections[option.key]);

      return value ? (arabic ? value.valueAr : value.valueEn) || '—' : '—';
    })
    .join(' / ');
}
